import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
    DataConnection,
    DataGlueGraph,
    DataValue,
    FunctionDefinition,
    FunctionPortDefinition,
    GlueVariable,
    NodeInstance,
    NodeInstanceDataPort,
    NodeInstanceState,
    UserDefinedType,
    Vector,
} from '@glueapp/graph-types';
import { areAllIntegrationsAndNodesForGraphPresent, areValidationsValid, processGraph } from '@glueapp/graph-validation';
import { NODE_LIBRARY, NODE_LIBRARY_DICTIONARY } from '@glueapp/graph-nodes';
import { GraphMetadata } from '../GraphList/types';
import { useGraphStorageApi } from '../api/useGraphStorageApi';
import {
    addConnection,
    addFunction,
    addFunctionInstance,
    addNode,
    addVariable,
    createFunctionWithInstance,
    deleteVariable,
    deselectAllNodes,
    deselectNodes,
    GraphReducerActions,
    layoutGraph,
    mergeInGraph,
    moveNodes,
    moveNodesEnd,
    PortPositions,
    removeConnection,
    removeNode,
    resizeNode,
    resizeNodeEnd,
    selectBranch,
    selectBranchAdd,
    selectNodes,
    selectOnlyOneNode,
    setNodeDimensions,
    setNodeShape,
    setNodeState,
    setPortValueAndType,
    setSelectedVariable,
    updateFunctionName,
    updateVariable,
} from './actions';
import { GraphEditorDrawing } from './drawingLayer/GraphEditorDrawing';
import { Container, MainSection, StyledTopBar } from './LayoutComponents';
import { useGraphExecution } from './useGraphExecution';
import { useWebhooks } from './useWebhooks';
import { useLiveSync } from './useLiveSync';
import { useIntegrationAccesses } from './useIntegrationAccesses';
import { useIntegrationAccessApi } from '../api/useIntegrationAccessApi';
import { ConnectionLookupProvider } from './useConnectionLookup';
import {
    type AIAgentLibraryElement,
    FunctionInstanceLibraryElement,
    LibraryElement,
    libraryNodeElementFromNode,
    NewFunctionLibraryElement,
} from '@/GraphEditor/drawingLayer/NodeLibrary/LibraryElement';
import { RunHistoryPanel } from './RunHistoryPanel';
import { RunInfoBar } from './RunInfoBar';
import { SecretsPanel } from '@/components/SecretsPanel/SecretsPanel';
import { useAgentSession } from './useAgentSession';
import { useAgentManager, computeSpatialContext } from './agent/useAgentManager';
import { getViewPortCenterFromGraphContainer } from './drawingLayer/helpers/getViewPortCenterFromGraphContainer';
import { getHighestIdFromObjectsWithIds } from './drawingLayer/helpers/getHighestIdFromObjectsWithIds';
import { useGraphRuns } from './useGraphRuns';
import { useRunViewing } from './useRunViewing';
import { NodeAnimationContext } from './contexts/NodeAnimationContext';
import { PortOverlayContent } from './drawingLayer/portOverlay/PortOverlayContent';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { setGraphName } from '@glueapp/graph-collab';
import { useYjsRoom } from './collab/useYjsRoom';
import { useYDocDispatch } from './collab/useYDocDispatch';
import { useYDocGraph } from './collab/useYDocGraph';
import { useYDocGraphName } from './collab/useYDocGraphName';
import { useAwareness, useCursorPublisher } from './collab/useAwareness';
import { useAwarenessSelectionPruner } from './collab/useAwarenessSelectionPruner';
import { CollaboratorAvatars } from './collab/CollaboratorAvatars';
interface Props {
    graphId: string;
}

export const GraphEditor: React.FC<Props> = ({ graphId }) => {
    const [functionId, setFunctionId] = useState<string | null>(null);
    const { getGraph, updateGraph } = useGraphStorageApi();
    const { integrationAccesses, deleteIntegrationAccess } = useIntegrationAccesses();
    const { callIntegration } = useIntegrationAccessApi();

    // Phase 3: Y.Doc is the source of truth.
    //   yDocGraph = useYDocGraph(yDoc) — derived JSON projection from the doc, with
    //               the local user's awareness selection merged in
    //   dataGraph = processGraph(yDocGraph) — validated/projected view used for render,
    //               save, execution, and agent reads
    const collabRoom = useYjsRoom(graphId);
    const yDocGraph = useYDocGraph(collabRoom?.yDoc ?? null, collabRoom?.awareness ?? null);

    const [graphMetaData, setGraphMetadata] = useState<GraphMetadata | undefined>(undefined);

    // The graph name lives in the Y.Doc (`meta` map) so renames sync across clients.
    // `graphMetaData` still supplies `description`; its `name` is only a pre-sync fallback.
    const yDocGraphName = useYDocGraphName(collabRoom?.yDoc ?? null);
    const effectiveMetadata = useMemo<GraphMetadata | undefined>(
        () => (graphMetaData ? { ...graphMetaData, name: yDocGraphName ?? graphMetaData.name } : undefined),
        [graphMetaData, yDocGraphName],
    );

    const processed = useMemo(() => {
        if (!yDocGraph || !integrationAccesses) return undefined;
        return processGraph(yDocGraph, NODE_LIBRARY, integrationAccesses);
    }, [yDocGraph, integrationAccesses]);
    const presentationGraph = processed?.graph;
    const validationResult = processed?.validationResult;
    const dataGraph = presentationGraph ?? yDocGraph;

    // Animation state for node position transitions (used during layout operations)
    const [isAnimating, setIsAnimating] = useState(false);
    const ANIMATION_DURATION_MS = 300;

    const viewportInfoRef = useRef<{
        containerRef: React.RefObject<HTMLDivElement>;
        zoomLevel: number;
    } | null>(null);

    const { enabled: aiEditEnabled } = useFeatureFlag('ai-agent-editing');

    // Viewport center for agent layout (used to position new agent-added nodes)
    const getViewportCenter = useCallback((): { x: number; y: number } => {
        const viewportInfo = viewportInfoRef.current;
        if (viewportInfo?.containerRef?.current) {
            return getViewPortCenterFromGraphContainer(viewportInfo.containerRef.current, viewportInfo.zoomLevel);
        }
        return { x: 20000, y: 20000 };
    }, []);

    // The Y.Doc dispatcher (single chokepoint for every mutation). Snapshot returns
    // the latest user-visible graph so the dispatcher can compute next ids, branch
    // selections, layout positions, etc. against what the user sees.
    const dataGraphRef = useRef(dataGraph);
    dataGraphRef.current = dataGraph;
    const getSnapshotForDispatch = useCallback(() => dataGraphRef.current, []);

    const dispatchGraphAction = useYDocDispatch({
        yDoc: collabRoom?.yDoc ?? null,
        awareness: collabRoom?.awareness ?? null,
        undoManager: collabRoom?.undoManager ?? null,
        getSnapshot: getSnapshotForDispatch,
    });

    const agentSession = useAgentSession({
        graphId,
        graph: dataGraph,
        metadata: effectiveMetadata ? { name: effectiveMetadata.name, description: effectiveMetadata.description } : undefined,
    });

    const agentManager = useAgentManager({
        graphId,
        graph: dataGraph,
        metadata: effectiveMetadata ? { name: effectiveMetadata.name, description: effectiveMetadata.description } : undefined,
    });

    // Wire undo/redo to the per-user `Y.UndoManager` constructed inside `useYjsRoom`.
    // Tracked origins on that manager are gated to the local clientID, so a user's
    // undo only affects their own writes.
    const undo = useCallback(() => {
        collabRoom?.undoManager?.undo();
    }, [collabRoom?.undoManager]);
    const redo = useCallback(() => {
        collabRoom?.undoManager?.redo();
    }, [collabRoom?.undoManager]);

    const collaboratorPresences = useAwareness(collabRoom?.awareness ?? null);
    const publishCursor = useCursorPublisher(collabRoom?.awareness ?? null);
    useAwarenessSelectionPruner(collabRoom?.yDoc ?? null, collabRoom?.awareness ?? null);
    const handleCursorMove = useCallback((graphPosition: Vector) => publishCursor(graphPosition.x, graphPosition.y), [publishCursor]);

    // Secrets panel state
    const [isSecretsPanelOpen, setIsSecretsPanelOpen] = useState(false);

    // Run history state
    const [isRunsPanelOpen, setIsRunsPanelOpen] = useState(false);
    const { runsQuery, fetchRunDetail, invalidateRunList } = useGraphRuns(graphId);
    const runViewing = useRunViewing(fetchRunDetail, dataGraph);

    // Track the last-saved graph to detect dirty state
    const lastSavedGraphRef = useRef<DataGlueGraph | undefined>(undefined);

    // Flush any pending save immediately (used before execution)
    const flushSave = useCallback(async () => {
        if (dataGraph && effectiveMetadata && dataGraph !== lastSavedGraphRef.current) {
            await updateGraph(graphId, { graph: dataGraph, metadata: effectiveMetadata });
            lastSavedGraphRef.current = dataGraph;
        }
    }, [dataGraph, effectiveMetadata, updateGraph, graphId]);

    // Create a save callback for webhooks that immediately persists the graph
    const saveGraphForWebhook = useCallback(async () => {
        if (dataGraph && effectiveMetadata) {
            await updateGraph(graphId, { graph: dataGraph, metadata: effectiveMetadata });
        }
    }, [dataGraph, effectiveMetadata, updateGraph, graphId]);

    const { webhooks, createWebhook, nodeInstancesWithPendingRequests } = useWebhooks(graphId, {
        saveGraph: saveGraphForWebhook,
    });

    // Fetch graph metadata only — the graph itself flows in via hocuspocus's `synced`
    // event and is reconstructed from the Y.Doc through `useYDocGraph`. The legacy
    // path used to also issue `dispatchGraphAction(setGraph(graphPackage.graph))`,
    // but that is no longer the source of truth.
    useEffect(() => {
        const fetchData = async () => {
            const graphPackage = await getGraph(graphId);
            setGraphMetadata(graphPackage.metadata);
            lastSavedGraphRef.current = graphPackage.graph;
        };
        fetchData();
    }, [graphId, getGraph]);

    const [graphValid, setGraphValid] = useState<boolean>(false);
    useEffect(() => {
        if (
            dataGraph &&
            integrationAccesses &&
            validationResult &&
            areValidationsValid(validationResult) &&
            areAllIntegrationsAndNodesForGraphPresent(dataGraph, NODE_LIBRARY, integrationAccesses)
        ) {
            setGraphValid(true);
        } else {
            setGraphValid(false);
        }
    }, [dataGraph, integrationAccesses, validationResult]);

    const { executionResult, executionError, triggerExecution, resetExecution, ranNodes, setExecutionRunning } = useGraphExecution(
        graphId,
        dataGraph,
        validationResult,
        graphValid,
        flushSave,
    );

    const handleRunCompleted = useCallback(
        (notification: { runId: string; graphId: string; duration: number; hasError: boolean; createdAt: string }) => {
            invalidateRunList();
            if (runViewing.livePreviewEnabled) {
                runViewing.viewRun(notification.runId);
            }
        },
        [invalidateRunList, runViewing.livePreviewEnabled, runViewing.viewRun],
    );

    useLiveSync(graphId, setExecutionRunning, handleRunCompleted);

    // Reset execution view when the source graph changes. Y.Doc-derived graph is
    // the source of truth; observe topology-relevant changes by reference equality.
    const lastSourceGraph = useRef<DataGlueGraph | undefined>(undefined);
    useEffect(() => {
        if (yDocGraph && yDocGraph !== lastSourceGraph.current) {
            lastSourceGraph.current = yDocGraph;
            if (!runViewing.isViewingRun) {
                resetExecution();
            }
        }
    }, [yDocGraph, runViewing.isViewingRun, resetExecution]);

    const dispatchUpdatePortValue = useCallback(
        (nodeInstanceId: number, port: NodeInstanceDataPort, value: DataValue | undefined, userDefinedType?: UserDefinedType) => {
            if (functionId) {
                dispatchGraphAction(setPortValueAndType(nodeInstanceId, port, value, userDefinedType, functionId));
            } else {
                dispatchGraphAction(setPortValueAndType(nodeInstanceId, port, value, userDefinedType));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchAddVariable = useCallback(
        (newVariable: GlueVariable) => dispatchGraphAction(addVariable(newVariable)),
        [dispatchGraphAction],
    );
    const dispatchUpdateVariable = useCallback(
        (oldName: string, variable: GlueVariable) => dispatchGraphAction(updateVariable(oldName, variable)),
        [dispatchGraphAction],
    );
    const dispatchDeleteVariable = useCallback((name: string) => dispatchGraphAction(deleteVariable(name)), [dispatchGraphAction]);

    const dispatchSetSelectedVariable = useCallback(
        (name: string | undefined, nodeInstanceId: number) => {
            if (functionId) {
                dispatchGraphAction(setSelectedVariable(name, nodeInstanceId, functionId));
            } else {
                dispatchGraphAction(setSelectedVariable(name, nodeInstanceId));
            }
        },
        [functionId, dispatchGraphAction],
    );

    // Function-related dispatchers
    const dispatchAddFunction = useCallback(
        (newFunction: FunctionDefinition) => dispatchGraphAction(addFunction(newFunction)),
        [dispatchGraphAction],
    );
    const dispatchCreateFunctionWithInstance = useCallback(
        (functionName: string, position: Vector) => {
            if (functionId) {
                dispatchGraphAction(createFunctionWithInstance(functionName, position, functionId));
            } else {
                dispatchGraphAction(createFunctionWithInstance(functionName, position));
            }
        },
        [functionId, dispatchGraphAction],
    );
    const dispatchAddFunctionInstance = useCallback(
        (targetFunctionId: string, position: Vector) => {
            if (functionId) {
                dispatchGraphAction(addFunctionInstance(targetFunctionId, position, functionId));
            } else {
                dispatchGraphAction(addFunctionInstance(targetFunctionId, position));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const contextData = useMemo(() => {
        if (functionId && dataGraph) {
            const func = dataGraph.functions?.find(f => f.id === functionId);
            return {
                nodeInstances: func?.nodeInstances || [],
                connections: func?.connections || [],
                variables: dataGraph.variables,
                currentFunctionId: functionId,
            };
        }
        return {
            nodeInstances: dataGraph?.nodeInstances || [],
            connections: dataGraph?.connections || [],
            variables: dataGraph?.variables || [],
            currentFunctionId: undefined,
        };
    }, [functionId, dataGraph]);

    const nodeDefinitions = NODE_LIBRARY_DICTIONARY;

    const extendedNodeLibrary: LibraryElement[] = useMemo(() => {
        const newFunctionNodeLibraryElement: NewFunctionLibraryElement = {
            type: 'NEW_FUNCTION',
            category: 'Functions',
            color: '#8B5CF6',
            description: 'Creates a new function',
            id: 'new-function',
            name: 'New Function',
            nodeId: 'function',
        };

        const functionNodes: FunctionInstanceLibraryElement[] =
            dataGraph?.functions?.map((func): FunctionInstanceLibraryElement => {
                return {
                    type: 'FUNCTION_INSTANCE',
                    functionId: func.id,
                    id: `user-function-${func.id}`,
                    name: 'Function: ' + func.name,
                    description: 'Pull in a new instance of the function',
                    category: 'Functions',
                    color: '#8B5CF6',
                    nodeId: 'function',
                };
            }) ?? [];

        const elements: LibraryElement[] = [];

        if (aiEditEnabled) {
            const aiAgentElement: AIAgentLibraryElement = {
                type: 'AI_AGENT',
                category: 'AI',
                color: '#3b82f6',
                description: 'Spawn an AI agent to build or modify your graph',
                id: '__ai-agent__',
                name: 'AI Agent',
                nodeId: '__ai-agent__',
            };
            elements.push(aiAgentElement);
        }

        elements.push(
            newFunctionNodeLibraryElement,
            ...functionNodes,
            ...NODE_LIBRARY.filter(node => node.findableInLibrary).map(libraryNodeElementFromNode),
        );

        return elements;
    }, [dataGraph?.functions, aiEditEnabled]);

    const dispatchAddNode = useCallback(
        (libraryElement: LibraryElement, position: Vector) => {
            switch (libraryElement.type) {
                case 'AI_AGENT':
                    agentManager.spawnAgent(position, functionId);
                    break;
                case 'LIBRARY_NODE':
                    if (functionId) {
                        dispatchGraphAction(addNode(libraryElement.nodeId, position, functionId));
                    } else {
                        dispatchGraphAction(addNode(libraryElement.nodeId, position));
                    }
                    break;
                case 'NEW_FUNCTION':
                    dispatchCreateFunctionWithInstance('New Function', position);
                    break;
                case 'FUNCTION_INSTANCE':
                    dispatchAddFunctionInstance(libraryElement.functionId, position);
                    break;
            }
        },
        [functionId, dispatchGraphAction, dispatchCreateFunctionWithInstance, dispatchAddFunctionInstance, agentManager],
    );

    const dispatchAddConnection = useCallback(
        (connection: Omit<DataConnection, 'id'>) => {
            if (functionId) {
                dispatchGraphAction(addConnection(connection, functionId));
            } else {
                dispatchGraphAction(addConnection(connection));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchRemoveConnection = useCallback(
        (index: number) => {
            if (functionId) {
                dispatchGraphAction(removeConnection(index, functionId));
            } else {
                dispatchGraphAction(removeConnection(index));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchMergeInGraph = useCallback(
        (nodeInstances: NodeInstance[], connections: DataConnection[], deselectOld: boolean = true): number[] => {
            const currentGraph = dataGraphRef.current;
            const targetNodeInstances = functionId
                ? (currentGraph?.functions?.find(f => f.id === functionId)?.nodeInstances ?? currentGraph?.nodeInstances ?? [])
                : (currentGraph?.nodeInstances ?? []);
            const targetConnections = functionId
                ? (currentGraph?.functions?.find(f => f.id === functionId)?.connections ?? currentGraph?.connections ?? [])
                : (currentGraph?.connections ?? []);
            const highestNodeId = getHighestIdFromObjectsWithIds(targetNodeInstances) || 0;
            const highestConnectionId = getHighestIdFromObjectsWithIds(targetConnections) || 0;
            const assignedIds = nodeInstances.map((_, index) => highestNodeId + 1 + index);
            const oldIdToNewId: { [key: number]: number } = Object.fromEntries(
                nodeInstances.map((node, index) => [node.id, assignedIds[index]]),
            );
            const translatedNodes: NodeInstance[] = nodeInstances.map(node => {
                const definition = NODE_LIBRARY_DICTIONARY[node.nodeId];
                const transientKeys = definition?.transientStateKeys;
                let state = node.state;
                if (transientKeys && transientKeys.length > 0) {
                    state = { ...state };
                    for (const key of transientKeys) delete state[key];
                }
                return { ...node, id: oldIdToNewId[node.id], state };
            });
            const translatedConnections: DataConnection[] = connections.map((connection, index) => ({
                ...connection,
                id: highestConnectionId + 1 + index,
                sendingNodeInstanceId: oldIdToNewId[connection.sendingNodeInstanceId] ?? connection.sendingNodeInstanceId,
                receivingNodeInstanceId: oldIdToNewId[connection.receivingNodeInstanceId] ?? connection.receivingNodeInstanceId,
            }));
            if (functionId) {
                dispatchGraphAction(mergeInGraph(translatedNodes, translatedConnections, deselectOld, functionId));
            } else {
                dispatchGraphAction(mergeInGraph(translatedNodes, translatedConnections, deselectOld));
            }
            return assignedIds;
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchMoveNodes = useCallback(
        (delta: Vector) => {
            // Read selection from awareness (the source of truth post-Step 3) so the
            // mutator targets exactly the user's currently-selected nodes — even if
            // the selection changed between this drag's start and the move event.
            const rawSelection = collabRoom?.awareness?.getLocalState()?.selectedLegacyIds;
            const selectedLegacyIds: number[] = Array.isArray(rawSelection) ? (rawSelection as number[]) : [];
            if (functionId) {
                dispatchGraphAction(moveNodes(delta, selectedLegacyIds, functionId));
            } else {
                dispatchGraphAction(moveNodes(delta, selectedLegacyIds));
            }
        },
        [functionId, dispatchGraphAction, collabRoom?.awareness],
    );

    const dispatchMoveNodesEnd = useCallback(() => {
        if (functionId) {
            dispatchGraphAction(moveNodesEnd(functionId));
        } else {
            dispatchGraphAction(moveNodesEnd());
        }
    }, [functionId, dispatchGraphAction]);

    const dispatchRemoveNodes = useCallback(
        (nodeIds: number[]) => {
            if (functionId) {
                dispatchGraphAction(removeNode(nodeIds, functionId));
            } else {
                dispatchGraphAction(removeNode(nodeIds));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchLayoutGraph = useCallback(
        (portPositions?: PortPositions) => {
            // Trigger animation for smooth position transitions
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), ANIMATION_DURATION_MS);

            if (functionId) {
                dispatchGraphAction(layoutGraph(functionId, portPositions));
            } else {
                dispatchGraphAction(layoutGraph(undefined, portPositions));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSelectNodes = useCallback(
        (nodeIds: number[]) => {
            if (functionId) {
                dispatchGraphAction(selectNodes(nodeIds, functionId));
            } else {
                dispatchGraphAction(selectNodes(nodeIds));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSelectBranch = useCallback(
        (nodeId: number) => {
            if (functionId) {
                dispatchGraphAction(selectBranch(nodeId, functionId));
            } else {
                dispatchGraphAction(selectBranch(nodeId));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSelectBranchAdd = useCallback(
        (nodeId: number) => {
            if (functionId) {
                dispatchGraphAction(selectBranchAdd(nodeId, functionId));
            } else {
                dispatchGraphAction(selectBranchAdd(nodeId));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSelectOnlyOneNode = useCallback(
        (nodeId: number) => {
            if (functionId) {
                dispatchGraphAction(selectOnlyOneNode(nodeId, functionId));
            } else {
                dispatchGraphAction(selectOnlyOneNode(nodeId));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchDeselectAllNodes = useCallback(() => {
        if (functionId) {
            dispatchGraphAction(deselectAllNodes(functionId));
        } else {
            dispatchGraphAction(deselectAllNodes());
        }
    }, [functionId, dispatchGraphAction]);

    const dispatchDeselectNodes = useCallback(
        (nodeIds: number[]) => {
            if (functionId) {
                dispatchGraphAction(deselectNodes(nodeIds, functionId));
            } else {
                dispatchGraphAction(deselectNodes(nodeIds));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchResizeNode = useCallback(
        (nodeId: number, position: Vector, width: number, height: number) => {
            if (functionId) {
                dispatchGraphAction(resizeNode(nodeId, position, width, height, functionId));
            } else {
                dispatchGraphAction(resizeNode(nodeId, position, width, height));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchResizeNodeEnd = useCallback(() => {
        if (functionId) {
            dispatchGraphAction(resizeNodeEnd(functionId));
        } else {
            dispatchGraphAction(resizeNodeEnd());
        }
    }, [functionId, dispatchGraphAction]);

    const dispatchSetNodeDimensions = useCallback(
        (nodeInstanceId: number, width: number, height: number) => {
            if (functionId) {
                dispatchGraphAction(setNodeDimensions(nodeInstanceId, { x: width, y: height }, functionId));
            } else {
                dispatchGraphAction(setNodeDimensions(nodeInstanceId, { x: width, y: height }));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSetNodeState = useCallback(
        (nodeInstanceId: number, state: NodeInstanceState) => {
            if (functionId) {
                dispatchGraphAction(setNodeState(nodeInstanceId, state, functionId));
            } else {
                dispatchGraphAction(setNodeState(nodeInstanceId, state));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchSetNodeShape = useCallback(
        (nodeInstanceId: number, shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[] }) => {
            if (functionId) {
                dispatchGraphAction(setNodeShape(nodeInstanceId, shape, functionId));
            } else {
                dispatchGraphAction(setNodeShape(nodeInstanceId, shape));
            }
        },
        [functionId, dispatchGraphAction],
    );

    const dispatchUpdateFunctionInputs = useCallback(
        (functionId: string, inputPorts: FunctionPortDefinition[]) => {
            dispatchGraphAction({
                type: 'UPDATE_FUNCTION_INPUTS',
                functionId,
                inputPortDefinitions: inputPorts,
            });
        },
        [dispatchGraphAction],
    );

    const dispatchUpdateFunctionOutputs = useCallback(
        (functionId: string, outputPorts: FunctionPortDefinition[]) => {
            dispatchGraphAction({
                type: 'UPDATE_FUNCTION_OUTPUTS',
                functionId,
                outputPortDefinitions: outputPorts,
            });
        },
        [dispatchGraphAction],
    );

    const goToRootGraph = useCallback(() => {
        setFunctionId(null);
    }, [setFunctionId]);

    const dispatchUpdateFunctionName = useCallback(
        (functionId: string, name: string) => {
            dispatchGraphAction(updateFunctionName(functionId, name));
        },
        [dispatchGraphAction],
    );

    const dispatchGraphActionWithFunctionContext = useCallback(
        (action: GraphReducerActions) => {
            // If the action supports functionId and we're in a function context, add it
            if (functionId && 'functionId' in action && action.functionId === undefined) {
                dispatchGraphAction({ ...action, functionId });
            } else {
                dispatchGraphAction(action);
            }
        },
        [functionId, dispatchGraphAction],
    );

    const effectiveExecutionResult = runViewing.isViewingRun ? runViewing.viewedExecutionResult : executionResult;

    const effectiveExecutionError = runViewing.isViewingRun ? runViewing.viewedExecutionError : executionError;

    return (
        <Container>
            {dataGraph && collabRoom?.synced ? (
                <>
                    <StyledTopBar
                        graphName={effectiveMetadata?.name ?? ''}
                        onGraphNameChange={newGraphName => {
                            if (collabRoom?.yDoc) setGraphName(collabRoom.yDoc, newGraphName);
                        }}
                        onRunsClick={() => {
                            if (!isRunsPanelOpen && agentSession.isOpen) agentSession.close();
                            setIsRunsPanelOpen(prev => !prev);
                        }}
                        isRunsPanelOpen={isRunsPanelOpen}
                        onSecretsClick={() => setIsSecretsPanelOpen(prev => !prev)}
                        isSecretsPanelOpen={isSecretsPanelOpen}
                        onAIEditClick={
                            aiEditEnabled
                                ? () => {
                                      if (agentManager.agents.length > 0) {
                                          // Focus existing agent
                                          agentManager.focusOrSpawn(getViewportCenter(), functionId);
                                      } else {
                                          if (isRunsPanelOpen) setIsRunsPanelOpen(false);
                                          agentManager.spawnAgent(getViewportCenter(), functionId);
                                      }
                                  }
                                : undefined
                        }
                        isAIPanelOpen={agentManager.agents.length > 0}
                        functionName={functionId ? dataGraph.functions?.find(f => f.id === functionId)?.name : undefined}
                        onFunctionNameChange={functionId ? (name: string) => dispatchUpdateFunctionName(functionId, name) : undefined}
                        onBackToMainGraph={functionId ? goToRootGraph : undefined}
                    />
                    <MainSection>
                        <CollaboratorAvatars peers={collaboratorPresences} />
                        {dataGraph !== undefined && integrationAccesses ? (
                            <NodeAnimationContext.Provider value={{ isAnimating }}>
                                <ConnectionLookupProvider connections={contextData.connections}>
                                    <GraphEditorDrawing
                                        key={functionId ?? 'root'}
                                        nodeInstances={contextData.nodeInstances}
                                        connections={contextData.connections}
                                        agents={agentManager.agents}
                                        agentManager={agentManager}
                                        currentFunctionId={functionId}
                                        currentFunctionDefinition={
                                            functionId ? dataGraph.functions?.find(f => f.id === functionId) : undefined
                                        }
                                        updateFunctionInputs={dispatchUpdateFunctionInputs}
                                        updateFunctionOutputs={dispatchUpdateFunctionOutputs}
                                        addNode={dispatchAddNode}
                                        addConnection={dispatchAddConnection}
                                        removeConnection={dispatchRemoveConnection}
                                        mergeInGraph={dispatchMergeInGraph}
                                        moveNodes={dispatchMoveNodes}
                                        moveNodesEnd={dispatchMoveNodesEnd}
                                        removeNodes={dispatchRemoveNodes}
                                        layoutGraph={dispatchLayoutGraph}
                                        selectNodes={dispatchSelectNodes}
                                        selectBranch={dispatchSelectBranch}
                                        selectBranchAdd={dispatchSelectBranchAdd}
                                        selectOnlyOneNode={dispatchSelectOnlyOneNode}
                                        deselectAllNodes={dispatchDeselectAllNodes}
                                        deselectNodes={dispatchDeselectNodes}
                                        reframeNode={dispatchResizeNode}
                                        reframeNodeEnd={dispatchResizeNodeEnd}
                                        setNodeDimensions={dispatchSetNodeDimensions}
                                        setNodeState={dispatchSetNodeState}
                                        setNodeShape={dispatchSetNodeShape}
                                        nodeDefinitions={nodeDefinitions}
                                        nodeLibrary={extendedNodeLibrary}
                                        functions={dataGraph.functions ?? []}
                                        callIntegration={callIntegration}
                                        executionResult={effectiveExecutionResult}
                                        executionError={effectiveExecutionError}
                                        validationResult={validationResult}
                                        triggerManualRun={triggerExecution}
                                        undo={undo}
                                        redo={redo}
                                        ranNodes={ranNodes}
                                        integrationAccesses={integrationAccesses}
                                        navigateToFunction={setFunctionId}
                                        dispatchGraphAction={dispatchGraphActionWithFunctionContext}
                                        viewportInfoRef={viewportInfoRef}
                                        isViewingRun={runViewing.isViewingRun}
                                        livePreviewEnabled={runViewing.livePreviewEnabled}
                                        onToggleLivePreview={runViewing.toggleLivePreview}
                                        runDetail={runViewing.activeRunDetail}
                                        isLoadingRunDetail={runViewing.isLoadingRunDetail}
                                        onCloseRunView={runViewing.clearRunView}
                                        compatibility={runViewing.compatibility}
                                        cursorPeers={collaboratorPresences}
                                        onCursorMove={handleCursorMove}
                                        renderOverlayContent={(nodeInstance, portName, portSide, closeOverlay) => (
                                            <PortOverlayContent
                                                nodeInstance={nodeInstance}
                                                portName={portName}
                                                portSide={portSide}
                                                integrationAccesses={integrationAccesses ?? []}
                                                deleteIntegrationAccess={deleteIntegrationAccess}
                                                connections={dataGraph.connections}
                                                onValueChange={dispatchUpdatePortValue}
                                                variables={dataGraph.variables}
                                                setSelectedVariable={dispatchSetSelectedVariable}
                                                addVariable={dispatchAddVariable}
                                                updateVariable={dispatchUpdateVariable}
                                                deleteVariable={dispatchDeleteVariable}
                                                webhooks={webhooks}
                                                nodeInstancesWithPendingRequests={nodeInstancesWithPendingRequests}
                                                createWebhook={createWebhook}
                                                availableFunctions={dataGraph.functions || []}
                                                dispatchGraphUpdate={dispatchGraphActionWithFunctionContext}
                                                navigateToFunction={setFunctionId}
                                                graphId={graphId}
                                                nodeExecutionResult={effectiveExecutionResult?.[nodeInstance.id]}
                                                onCloseOverlay={closeOverlay}
                                                onOpenSecretsPanel={() => setIsSecretsPanelOpen(true)}
                                            />
                                        )}
                                    />
                                </ConnectionLookupProvider>
                            </NodeAnimationContext.Provider>
                        ) : null}
                        {isRunsPanelOpen && (
                            <RunHistoryPanel
                                runsQuery={runsQuery}
                                onClose={() => setIsRunsPanelOpen(false)}
                                onSelectRun={runViewing.viewRun}
                                activeRunId={runViewing.activeRunId}
                                livePreviewEnabled={runViewing.livePreviewEnabled}
                                onToggleLivePreview={runViewing.toggleLivePreview}
                            />
                        )}
                        {isSecretsPanelOpen && <SecretsPanel graphId={graphId} onClose={() => setIsSecretsPanelOpen(false)} />}
                    </MainSection>
                </>
            ) : null}
        </Container>
    );
};
